import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useAuthState } from '@/hooks/auth/useAuth';
import { createLogger } from '@/utils/logger';

const log = createLogger('SentryUserContext');

/**
 * SentryUserContext Component
 *
 * Sets user context in Sentry when authentication state changes.
 * This enables Sentry to capture user information (id, email, name)
 * with error reports for easier debugging.
 *
 * Place this component inside the auth provider but outside error boundaries
 * so it can track user context throughout the session.
 *
 * @example
 * ```tsx
 * <ClientProvider>
 *   <SentryUserContext />
 *   <Sentry.ErrorBoundary fallback={<SentryErrorFallback />}>
 *     <RouterProvider router={router} />
 *   </Sentry.ErrorBoundary>
 * </ClientProvider>
 * ```
 */
export function SentryUserContext() {
  const { data: authState } = useAuthState();

  // Pre-extract only the sub-fields the effect actually cares about, so
  // the effect body never touches the full `authState` object and ESLint's
  // exhaustive-deps rule can correctly validate the dep array without a
  // suppression. This preserves the original behavioral intent: re-run
  // Sentry scope updates ONLY when one of these three fields changes —
  // NOT on every authState reference change (e.g. silent token refresh).
  const isAuthenticated = authState?.isAuthenticated;
  const user = authState?.user;
  const userType = authState?.userType;

  useEffect(() => {
    if (isAuthenticated && user) {
      // Set user context in Sentry
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || undefined,
      });

      // Add user type as a tag for filtering
      Sentry.setTag('user.type', userType || 'unknown');

      log.debug('Sentry user context set:', {
        id: user.id,
        email: user.email,
        userType,
      });
    } else {
      // Clear user context when logged out
      Sentry.setUser(null);
      Sentry.setTag('user.type', null);
      log.debug('Sentry user context cleared');
    }
  }, [isAuthenticated, user, userType]);

  // This component doesn't render anything
  return null;
}
